import { Phone, ArrowRight, Shield, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { PHONE_HREF } from "@/lib/contact";
import ParticleField from "@/components/ParticleField";

const stats = [
  { label: "Clientes activos",     num: 500, prefix: "+", suffix: "" },
  { label: "Años de experiencia",  num: 30,  prefix: "+", suffix: "" },
  { label: "Áreas de servicio",    num: 4,   prefix: "",  suffix: "" },
  { label: "Tasa de fidelización", num: 98,  prefix: "",  suffix: "%" },
];

const services = [
  { num: "01", name: "Fiscal",   pct: 92 },
  { num: "02", name: "Laboral",  pct: 78 },
  { num: "03", name: "Contable", pct: 85 },
  { num: "04", name: "Jurídica", pct: 70 },
];

export default function Hero() {
  const sectionRef     = useRef(null);
  const badgeRef       = useRef(null);
  const titleLine1Ref  = useRef(null);
  const titleLine2Ref  = useRef(null);
  const separatorRef   = useRef(null);
  const subtitleRef    = useRef(null);
  const ctasRef        = useRef(null);
  const visualRef      = useRef(null);
  const serviceListRef = useRef(null);
  const statsRowRef    = useRef(null);
  const shape1Ref      = useRef(null);
  const shape2Ref      = useRef(null);
  const shape3Ref      = useRef(null);
  const glowOrb1Ref    = useRef(null);
  const glowOrb2Ref    = useRef(null);

  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let onMouseMove;
    const ctx = gsap.context(() => {

      if (prefersReduced) {
        const targets = [
          badgeRef, titleLine1Ref, titleLine2Ref, separatorRef, subtitleRef, visualRef,
          ...(ctasRef.current ? Array.from(ctasRef.current.children).map(el => ({ current: el })) : []),
        ].filter(r => r?.current).map(r => r.current);
        gsap.set(targets, { opacity: 1, clearProps: "all" });
        return;
      }

      // ── Floating gradient orbs (ambient glow, option D) ──────────────────
      gsap.to(glowOrb1Ref.current, {
        x: 90, y: -70, duration: 10, ease: "sine.inOut", yoyo: true, repeat: -1,
      });
      gsap.to(glowOrb2Ref.current, {
        x: -80, y: 100, duration: 13, ease: "sine.inOut", yoyo: true, repeat: -1,
      });

      // ── Entrance timeline ─────────────────────────────────────────────────
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

      tl.fromTo(badgeRef.current,
          { opacity: 0, scaleX: 0.6, transformOrigin: "left center" },
          { opacity: 1, scaleX: 1, duration: 0.7, ease: "power2.out" })
        .fromTo(titleLine1Ref.current,
          { y: "115%", rotationX: -80, filter: "blur(6px)" },
          { y: "0%", rotationX: 0, filter: "blur(0px)", duration: 1, ease: "power3.out" }, "-=0.15")
        .fromTo(titleLine2Ref.current,
          { y: "115%", rotationX: -80, filter: "blur(6px)" },
          { y: "0%", rotationX: 0, filter: "blur(0px)", duration: 1, ease: "power3.out" }, "-=0.75")
        .fromTo(separatorRef.current,
          { scaleX: 0 },
          { scaleX: 1, duration: 0.55, transformOrigin: "left center" }, "-=0.4")
        .fromTo(subtitleRef.current,
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.7 }, "-=0.25")
        .fromTo(Array.from(ctasRef.current?.children ?? []),
          { opacity: 0, scale: 0.92 },
          { opacity: 1, scale: 1, stagger: 0.13, duration: 0.55 }, "-=0.45");

      if (visualRef.current) {
        tl.fromTo(visualRef.current,
          { opacity: 0, x: 50 },
          { opacity: 1, x: 0, duration: 0.9, ease: "power2.out" }, 0.35);
      }

      // Stagger service items inside the card (option C)
      if (serviceListRef.current) {
        tl.fromTo(
          Array.from(serviceListRef.current.children),
          { opacity: 0, x: 18 },
          { opacity: 1, x: 0, duration: 0.38, stagger: 0.09, ease: "power2.out", clearProps: "x" },
          "-=0.4"
        );
      }

      // ── Continuous animated dashboard (option C) ─────────────────────────
      if (visualRef.current) {
        // Progress bars breathing
        const bars = visualRef.current.querySelectorAll(".progress-bar-fill");
        bars.forEach((bar, i) => {
          gsap.fromTo(bar,
            { scaleX: 0.72, opacity: 0.45 },
            {
              scaleX: 1, opacity: 1,
              duration: 2.2 + i * 0.35,
              ease: "sine.inOut",
              yoyo: true,
              repeat: -1,
              delay: 1.8 + i * 0.45,
            }
          );
        });

        // Sparkline continuous draw
        const sparkPath = visualRef.current.querySelector(".sparkline-path");
        if (sparkPath) {
          const len = sparkPath.getTotalLength();
          gsap.set(sparkPath, { strokeDasharray: len, strokeDashoffset: len });
          gsap.to(sparkPath, {
            strokeDashoffset: 0,
            duration: 3,
            ease: "power1.inOut",
            repeat: -1,
            yoyo: true,
            repeatDelay: 0.4,
            delay: 2,
          });
        }

        // Parallax scroll on visual card
        gsap.to(visualRef.current, {
          yPercent: -12,
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top top",
            end: "bottom top",
            scrub: 1.4,
          },
        });
      }

      // ── Stats strip ───────────────────────────────────────────────────────
      if (statsRowRef.current) {
        // Tile entrance
        gsap.fromTo(
          statsRowRef.current.querySelectorAll("[data-stat-item]"),
          { opacity: 0, y: 22 },
          {
            opacity: 1, y: 0, duration: 0.45, stagger: 0.1,
            ease: "power2.out", clearProps: "y",
            scrollTrigger: {
              trigger: statsRowRef.current,
              start: "top bottom-=60px",
              toggleActions: "play none none none",
            },
          }
        );

        // Count-up
        statsRowRef.current.querySelectorAll("[data-stat-item]").forEach((el, i) => {
          const stat  = stats[i];
          const numEl = el.querySelector(".stat-num");
          if (!numEl) return;
          const obj = { val: 0 };
          gsap.to(obj, {
            val: stat.num,
            duration: 1.8,
            ease: "power2.out",
            scrollTrigger: { trigger: el, start: "top bottom" },
            onUpdate() {
              numEl.textContent = stat.prefix + Math.round(obj.val) + stat.suffix;
            },
          });
        });
      }

      // ── Mouse parallax on geometric shapes ───────────────────────────────
      onMouseMove = (e) => {
        const dx = (e.clientX - window.innerWidth  / 2) / window.innerWidth;
        const dy = (e.clientY - window.innerHeight / 2) / window.innerHeight;
        [shape1Ref, shape2Ref, shape3Ref].forEach((ref, i) => {
          if (!ref.current) return;
          const f = (i + 1) * 14;
          gsap.to(ref.current, { x: dx * f, y: dy * f, duration: 1.2, ease: "power2.out" });
        });
      };
      window.addEventListener("mousemove", onMouseMove);
    }, sectionRef);

    return () => {
      ctx.revert();
      if (onMouseMove) window.removeEventListener("mousemove", onMouseMove);
    };
  }, []);

  return (
    <section
      id="inicio"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col bg-[#1a1a1a] overflow-hidden">

      {/* Option D — Animated gradient orbs */}
      <div
        ref={glowOrb1Ref}
        className="absolute top-[10%] left-[5%] w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(201,169,110,0.06) 0%, transparent 70%)" }}
      />
      <div
        ref={glowOrb2Ref}
        className="absolute bottom-[5%] right-[10%] w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(201,169,110,0.04) 0%, transparent 70%)" }}
      />

      {/* Option B — Particle field */}
      <ParticleField />

      {/* Geometric background */}
      <div className="absolute inset-0 pointer-events-none">
        <div ref={shape1Ref} className="absolute top-[10%] right-[5%] w-[500px] h-[500px] rounded-full border border-[#c9a96e]/[0.04]" />
        <div ref={shape2Ref} className="absolute top-[30%] right-[15%] w-[300px] h-[300px] rounded-full border border-[#c9a96e]/[0.04]" />
        <div ref={shape3Ref} className="absolute bottom-[10%] left-[5%] w-[400px] h-[400px] rounded-full border border-[#c9a96e]/[0.03]" />
      </div>

      <div className="absolute left-8 top-1/4 w-[1px] h-40 bg-gradient-to-b from-transparent via-[#c9a96e]/40 to-transparent hidden xl:block" />

      <div className="flex-1 flex items-center relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 w-full pt-28 pb-12 md:pt-36 md:pb-16">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">

            {/* Text Column */}
            <div className="lg:col-span-7 space-y-10">
              <div ref={badgeRef} style={{ opacity: 0 }}>
                <span
                  data-testid="hero-badge"
                  className="inline-flex items-center gap-2.5 text-[#c9a96e] text-[10px] uppercase tracking-[0.3em] font-medium border border-[#c9a96e]/25 rounded-sm px-5 py-2.5">
                  <Shield className="w-3.5 h-3.5" />
                  Asesoría fiscal y jurídica
                </span>
              </div>

              <div>
                <h1
                  data-testid="hero-title"
                  className="text-[2.5rem] sm:text-5xl lg:text-[3.75rem] xl:text-7xl font-light tracking-[-0.02em] leading-[1.08] text-[#f8f6f3]"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  <div className="overflow-hidden">
                    <div ref={titleLine1Ref}>La asesoría que</div>
                  </div>
                  <div className="overflow-hidden">
                    <div ref={titleLine2Ref}>
                      <span className="italic font-normal text-[#c9a96e]">empresas</span>{" "}y{" "}
                      <span className="italic font-normal text-[#c9a96e]">autónomos</span>{" "}
                      necesitan
                    </div>
                  </div>
                </h1>
              </div>

              <div>
                <div ref={separatorRef} className="w-20 h-[2px] bg-[#c9a96e]/60" style={{ transformOrigin: "left center" }} />
              </div>

              <div ref={subtitleRef} style={{ opacity: 0 }}>
                <p
                  data-testid="hero-subtitle"
                  className="text-base md:text-[1.1rem] text-[#f8f6f3]/50 font-light leading-[1.8] max-w-lg"
                  style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Asesoramiento fiscal, laboral, contable y jurídico con trato cercano y personalizado en Alhaurín El Grande y toda la provincia de Málaga. Contacta sin compromiso.
                </p>
              </div>

              <div ref={ctasRef} className="flex flex-col sm:flex-row gap-4">
                <a href={PHONE_HREF} data-testid="hero-cta-call" style={{ opacity: 0 }}>
                  <Button
                    size="lg"
                    className="bg-[#c0392b] hover:bg-[#a93226] text-white rounded-sm uppercase tracking-[0.2em] text-[11px] font-semibold h-14 px-10 transition-all duration-300 shadow-lg shadow-[#c0392b]/20 hover:shadow-xl hover:shadow-[#c0392b]/40 hover:-translate-y-px w-full sm:w-auto">
                    <Phone className="w-4 h-4 mr-2.5" />
                    Llamar ahora
                  </Button>
                </a>
                <a
                  href="#contacto"
                  onClick={(e) => { e.preventDefault(); document.querySelector("#contacto")?.scrollIntoView({ behavior: "smooth" }); }}
                  data-testid="hero-cta-info"
                  style={{ opacity: 0 }}>
                  <Button
                    size="lg"
                    className="bg-[#c9a96e]/10 border border-[#c9a96e]/40 text-[#c9a96e] hover:bg-[#c9a96e] hover:text-[#1a1a1a] rounded-sm uppercase tracking-[0.2em] text-[11px] font-semibold h-14 px-10 transition-all duration-300 shadow-sm shadow-[#c9a96e]/0 hover:shadow-lg hover:shadow-[#c9a96e]/25 hover:-translate-y-px w-full sm:w-auto">
                    <ArrowRight className="w-4 h-4 mr-2.5" />
                    Solicitar información
                  </Button>
                </a>
              </div>
            </div>

            {/* Option C — Animated dashboard card */}
            <div ref={visualRef} className="hidden lg:block lg:col-span-5" style={{ opacity: 0 }}>
              <div className="relative ml-auto max-w-sm xl:max-w-md">
                <div className="absolute -inset-4 border border-[#c9a96e]/10 rounded-sm" />
                <div className="relative bg-[#1f1f1f] border border-[#c9a96e]/15 rounded-sm overflow-hidden">
                  <div className="h-1 bg-gradient-to-r from-[#c9a96e]/0 via-[#c9a96e] to-[#c9a96e]/0" />
                  <div className="p-8 xl:p-10 space-y-7">

                    {/* Rotating rings + logo */}
                    <div className="relative flex items-center justify-center h-32">
                      {/* Outer ring — slow CW */}
                      <div className="absolute w-32 h-32 spin-ring-cw" style={{ willChange: "transform" }}>
                        <svg viewBox="0 0 128 128" className="w-full h-full">
                          <circle cx="64" cy="64" r="60" fill="none" stroke="#c9a96e"
                                  strokeWidth="0.8" strokeDasharray="85 291"
                                  strokeLinecap="round" opacity="0.2" />
                        </svg>
                      </div>
                      {/* Inner ring — faster CCW */}
                      <div className="absolute w-20 h-20 spin-ring-ccw" style={{ willChange: "transform" }}>
                        <svg viewBox="0 0 80 80" className="w-full h-full">
                          <circle cx="40" cy="40" r="36" fill="none" stroke="#c9a96e"
                                  strokeWidth="1.2" strokeDasharray="48 178"
                                  strokeLinecap="round" opacity="0.38" />
                        </svg>
                      </div>
                      {/* Logo */}
                      <div className="relative z-10 flex items-center gap-1">
                        <img src="/escudo.png" alt="A.F. Puertos" className="h-12 w-auto object-contain" />
                      </div>
                    </div>

                    <div className="h-[1px] bg-[#c9a96e]/10" />

                    {/* Service rows with animated progress bars */}
                    <div className="space-y-4" ref={serviceListRef}>
                      {services.map((s) => (
                        <div key={s.num}>
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-3">
                              <span className="text-[#c9a96e]/40 text-[10px] font-mono tracking-wider">{s.num}</span>
                              <span className="text-[#f8f6f3]/60 text-sm font-light tracking-wide">{s.name}</span>
                            </div>
                            <span className="text-[#c9a96e]/45 text-[10px] font-mono">{s.pct}%</span>
                          </div>
                          <div className="h-[2px] bg-[#c9a96e]/8 rounded-full overflow-hidden">
                            <div
                              className="progress-bar-fill h-full bg-gradient-to-r from-[#c9a96e]/50 to-[#c9a96e] rounded-full"
                              style={{ width: `${s.pct}%`, transformOrigin: "left center" }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="h-[1px] bg-[#c9a96e]/10" />

                    {/* Sparkline */}
                    <div>
                      <p className="text-[#f8f6f3]/20 text-[9px] uppercase tracking-[0.3em] mb-2.5"
                         style={{ fontFamily: "'Manrope', sans-serif" }}>
                        Actividad
                      </p>
                      <svg viewBox="0 0 200 36" className="w-full h-8" preserveAspectRatio="none">
                        <defs>
                          <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#c9a96e" stopOpacity="0.15" />
                            <stop offset="100%" stopColor="#c9a96e" stopOpacity="0" />
                          </linearGradient>
                        </defs>
                        <path
                          d="M0,28 L18,22 L36,26 L54,14 L72,18 L90,8 L108,15 L126,10 L144,13 L162,5 L180,9 L200,3 L200,36 L0,36 Z"
                          fill="url(#sparkGrad)"
                        />
                        <path
                          className="sparkline-path"
                          d="M0,28 L18,22 L36,26 L54,14 L72,18 L90,8 L108,15 L126,10 L144,13 L162,5 L180,9 L200,3"
                          fill="none"
                          stroke="#c9a96e"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          opacity="0.55"
                        />
                      </svg>
                    </div>

                    <p className="text-[#f8f6f3]/20 text-[9px] uppercase tracking-[0.3em] text-center"
                       style={{ fontFamily: "'Manrope', sans-serif" }}>
                      Confianza &middot; Cercanía &middot; Compromiso
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="relative border-t border-[#c9a96e]/10 z-10" ref={statsRowRef}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#c9a96e]/10">
            {stats.map((stat, idx) => (
              <div
                key={stat.label}
                data-stat-item
                data-testid={`hero-stat-${idx}`}
                className="py-6 md:py-8 px-4 md:px-8 text-center">
                <p className="text-2xl md:text-3xl font-light text-[#c9a96e]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  <span className="stat-num">{stat.prefix}0{stat.suffix}</span>
                </p>
                <p className="text-[10px] md:text-[11px] text-[#f8f6f3]/35 uppercase tracking-[0.15em] mt-1.5 font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 animate-bounce hidden md:block opacity-30 z-10">
        <ChevronDown className="w-5 h-5 text-[#c9a96e]" />
      </div>
    </section>
  );
}
